import React from "react";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Paperclip, Square, X, Send, Plus, Library, FileText, Image, FolderOpen, ChevronDown, Check, Mic, Loader2, Settings2, GripVertical, Undo2, Pencil } from "lucide-react";

import { motion, AnimatePresence } from "framer-motion";
import { validateMultipleFiles } from "@/utils/fileProcessing";
import { CHAT_ATTACHMENT_LIMITS, formatChatAttachmentSize } from "@hector/shared-types";
import { convertHeicFilesToJpeg, isHeicFile } from "@/utils/heicConversion";
import { expandChatAttachmentArchives, isZipFile, type SkippedArchiveEntry } from "@/utils/zipArchiveUpload";
import { DEFAULT_DOCUMENT_MULTI_CONTEXT } from "@/types/documentContext";
import type { DocumentContextMultiFilter } from "@/types/documentContext";
import { toast } from "sonner";
import { toastDuration } from "@/components/ui/sonner";
import { useVoiceRecording } from "@/hooks/useVoiceRecording";
import type { UserPrompt } from '@/features/hector-agent-casediscovery-v3/api/userPrompts.api';
import { AddPromptForm } from '@/features/hector-agent-casediscovery-v3/components/prompt-library/AddPromptForm';
import {
  ConversationMemoryControl,
  type ConversationMemoryUsage,
} from '@/features/hector-agent-casediscovery-v3/components/common/ConversationMemoryControl';
import type { ContextCompactionState } from '@/features/hector-agent-casediscovery-v3/types/session.types';
import { mergeReturnedFiles, submitComposerDraft, type ComposerSendOutcome } from './composerSubmission';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  arrayMove,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  SkillSlashPalette,
  filterSkills,
  getSlashToken,
  keepActiveSkillSlug,
  moveActiveSkillSlug,
  replaceSlashToken,
  sortSkillsForPalette,
  useSkillCatalog,
} from '@/features/agent-skills';
import type { ResolvedSkillMetadata } from '@hector/shared-types';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { distinctiveCaseLabels } from './caseChipLabels';

// Utility function for className merging
const cn = (...classes: (string | undefined | null | false)[]) => classes.filter(Boolean).join(" ");

// Embedded CSS for minimal custom styles
const styles = `
  *:focus-visible {
    outline-offset: 0 !important;
    --ring-offset: 0 !important;
  }
  textarea:focus,
  textarea:focus-visible,
  textarea:focus-within {
    outline: none !important;
    box-shadow: none !important;
    border-color: transparent !important;
  }
  textarea::-webkit-scrollbar {
    width: 6px;
  }
  textarea::-webkit-scrollbar-track {
    background: transparent;
  }
  textarea::-webkit-scrollbar-thumb {
    background-color: #666666;
    border-radius: 3px;
  }
  textarea::-webkit-scrollbar-thumb:hover {
    background-color: #999999;
  }
  @keyframes voice-pulse {
    0%, 100% { transform: scale(1); opacity: 1; }
    50% { transform: scale(1.08); opacity: 0.9; }
  }
  @keyframes voice-ring {
    0% { transform: scale(1); opacity: 0.4; }
    100% { transform: scale(2); opacity: 0; }
  }
  @keyframes voice-bar {
    0%, 100% { height: 3px; }
    50% { height: 14px; }
  }
  .voice-pulse { animation: voice-pulse 2s ease-in-out infinite; }
  .voice-ring { animation: voice-ring 2s ease-out infinite; }
`;

// Inject styles into document
const styleSheet = document.createElement("style");
styleSheet.innerText = styles;
document.head.appendChild(styleSheet);

// Textarea Component
interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  className?: string;
}
const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(({ className, ...props }, ref) => (
  <textarea
    className={cn(
      "flex w-full rounded-md border-none bg-transparent px-3 py-2 text-sm text-[#262626] placeholder:text-[#666666] focus:outline-none focus:ring-0 focus:border-none focus-visible:outline-none focus-visible:ring-0 outline-none ring-0 disabled:cursor-not-allowed disabled:opacity-50 min-h-[40px] resize-none scrollbar-thin scrollbar-thumb-[#666666] scrollbar-track-transparent hover:scrollbar-thumb-[#999999]",
      className
    )}
    ref={ref}
    rows={1}
    {...props}
  />
));
Textarea.displayName = "Textarea";

// Tooltip Components
const TooltipProvider = TooltipPrimitive.Provider;
const Tooltip = TooltipPrimitive.Root;
const TooltipTrigger = TooltipPrimitive.Trigger;
const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(({ className, sideOffset = 4, ...props }, ref) => (
  <TooltipPrimitive.Content
    ref={ref}
    sideOffset={sideOffset}
    className={cn(
      "z-50 overflow-hidden rounded-md border border-[#D9D9D9] bg-white px-3 py-1.5 text-sm text-[#262626] shadow-sm animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
      className
    )}
    {...props}
  />
));
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

// Dialog Components
const Dialog = DialogPrimitive.Root;
const DialogPortal = DialogPrimitive.Portal;
const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-black/60 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

type DialogContentProps = React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
  /** Masque le bouton de fermeture en haut à droite (ex: modal piloté par AddPromptForm avec ses propres boutons) */
  hideCloseButton?: boolean;
};

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  DialogContentProps
>(({ className, children, hideCloseButton, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        "fixed left-[50%] top-[50%] z-50 grid w-full max-w-[90vw] md:max-w-[800px] translate-x-[-50%] translate-y-[-50%] gap-4 border border-[#D9D9D9] bg-white p-0 shadow-xl duration-300 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 rounded-2xl",
        className
      )}
      {...props}
    >
      {children}
      {!hideCloseButton && (
        <DialogPrimitive.Close className="absolute right-4 top-4 z-10 rounded-full bg-[#F7F7F7]/80 p-2 hover:bg-[#F7F7F7] transition-all">
          <X className="h-5 w-5 text-[#666666] hover:text-[#262626]" />
          <span className="sr-only">Close</span>
        </DialogPrimitive.Close>
      )}
    </DialogPrimitive.Content>
  </DialogPortal>
));
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn("text-base font-medium leading-none tracking-tight text-[#262626]", className)}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

// Button Component
interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "default" | "outline" | "ghost";
  size?: "default" | "sm" | "lg" | "icon";
}
const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = "default", size = "default", ...props }, ref) => {
    const variantClasses = {
      default: "bg-[#262626] hover:bg-[#333333] text-white",
      outline: "border border-[#D9D9D9] bg-transparent hover:bg-[#F7F7F7]",
      ghost: "bg-transparent hover:bg-[#F7F7F7]",
    };
    const sizeClasses = {
      default: "h-10 px-4 py-2",
      sm: "h-8 px-3 text-sm",
      lg: "h-12 px-6",
      icon: "h-8 w-8 rounded-full aspect-[1/1]",
    };
    return (
      <button
        className={cn(
          "inline-flex items-center justify-center font-medium transition-colors focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50",
          variantClasses[variant],
          sizeClasses[size],
          className
        )}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = "Button";

// Custom Divider Component

// PromptInput Context and Components
interface PromptInputContextType {
  isLoading: boolean;
  value: string;
  setValue: (value: string) => void;
  maxHeight: number | string;
  onSubmit?: () => void;
  disabled?: boolean;
}
const PromptInputContext = React.createContext<PromptInputContextType>({
  isLoading: false,
  value: "",
  setValue: () => {},
  maxHeight: 240,
  onSubmit: undefined,
  disabled: false,
});
function usePromptInput() {
  const context = React.useContext(PromptInputContext);
  if (!context) throw new Error("usePromptInput must be used within a PromptInput");
  return context;
}

interface PromptInputProps {
  isLoading?: boolean;
  value?: string;
  onValueChange?: (value: string) => void;
  maxHeight?: number | string;
  onSubmit?: () => void;
  children: React.ReactNode;
  className?: string;
  disabled?: boolean;
  onDragOver?: (e: React.DragEvent) => void;
  onDragLeave?: (e: React.DragEvent) => void;
  onDrop?: (e: React.DragEvent) => void;
}
const PromptInput = React.forwardRef<HTMLDivElement, PromptInputProps>(
  (
    {
      className,
      isLoading = false,
      maxHeight = 240,
      value,
      onValueChange,
      onSubmit,
      children,
      disabled = false,
      onDragOver,
      onDragLeave,
      onDrop,
    },
    ref
  ) => {
    const [internalValue, setInternalValue] = React.useState(value || "");
    const handleChange = (newValue: string) => {
      setInternalValue(newValue);
      onValueChange?.(newValue);
    };
    return (
      <TooltipProvider>
        <PromptInputContext.Provider
          value={{
            isLoading,
            value: value ?? internalValue,
            setValue: onValueChange ?? handleChange,
            maxHeight,
            onSubmit,
            disabled,
          }}
        >
          <div
            ref={ref}
            className={cn(
              "@container rounded-3xl border border-[#D9D9D9] bg-white p-2 shadow-[0_8px_30px_rgba(0,0,0,0.12)] transition-all duration-300 min-w-0",
              className
            )}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
            data-tour="assistant-composer"
          >
            {children}
          </div>
        </PromptInputContext.Provider>
      </TooltipProvider>
    );
  }
);
PromptInput.displayName = "PromptInput";

interface PromptInputTextareaProps {
  disableAutosize?: boolean;
  placeholder?: string;
}
const PromptInputTextarea: React.FC<PromptInputTextareaProps & React.ComponentProps<typeof Textarea>> = ({
  className,
  onKeyDown,
  disableAutosize = false,
  placeholder,
  ...props
}) => {
  const { value, setValue, maxHeight, onSubmit, disabled, isLoading } = usePromptInput();
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);

  React.useEffect(() => {
    if (disableAutosize || !textareaRef.current) return;
    if (value === "") {
      textareaRef.current.style.height = "";
      return;
    }
    textareaRef.current.style.height = "auto";
    textareaRef.current.style.height =
      typeof maxHeight === "number"
        ? `${Math.min(textareaRef.current.scrollHeight, maxHeight)}px`
        : `min(${textareaRef.current.scrollHeight}px, ${maxHeight})`;
  }, [value, maxHeight, disableAutosize]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Slash-command consumers get first refusal. A selected skill must only
    // complete the token; it must never submit the lawyer's unfinished prompt.
    onKeyDown?.(e);
    if (e.defaultPrevented) return;
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      // La zone reste éditable pendant que l'agent travaille (préparation du
      // prochain message), mais Entrée n'ENVOIE pas tant qu'un tour est en cours.
      // `isLoading` (contexte) est la seule garde d'envoi ; `disabled` ne sert plus
      // qu'à un vrai état désactivé du champ.
      if (!isLoading) onSubmit?.();
    }
  };

  return (
    <Textarea
      ref={textareaRef}
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onKeyDown={handleKeyDown}
      className={cn("text-sm", className)}
      disabled={disabled}
      placeholder={placeholder}
      {...props}
    />
  );
};

type PromptInputActionsProps = React.HTMLAttributes<HTMLDivElement>;
const PromptInputActions: React.FC<PromptInputActionsProps> = ({ children, className, ...props }) => (
  <div className={cn("flex items-center gap-2", className)} {...props}>
    {children}
  </div>
);

interface PromptInputActionProps extends React.ComponentProps<typeof Tooltip> {
  tooltip: React.ReactNode;
  children: React.ReactNode;
  side?: "top" | "bottom" | "left" | "right";
  className?: string;
}
const PromptInputAction: React.FC<PromptInputActionProps> = ({
  tooltip,
  children,
  className,
  side = "top",
  ...props
}) => {
  const { disabled } = usePromptInput();
  return (
    <Tooltip {...props}>
      <TooltipTrigger asChild disabled={disabled}>
        {children}
      </TooltipTrigger>
      <TooltipContent side={side} className={className}>
        {tooltip}
      </TooltipContent>
    </Tooltip>
  );
};

// ImageViewDialog Component
interface ImageViewDialogProps {
  imageUrl: string | null;
  onClose: () => void;
}
const ImageViewDialog: React.FC<ImageViewDialogProps> = ({ imageUrl, onClose }) => {
  if (!imageUrl) return null;
  return (
    <Dialog open={!!imageUrl} onOpenChange={onClose}>
      <DialogContent className="p-0 border-none bg-transparent shadow-none max-w-[90vw] md:max-w-[800px]">
        <DialogTitle className="sr-only">Image Preview</DialogTitle>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2, ease: "easeOut" }}
          className="relative bg-white rounded-2xl overflow-hidden shadow-2xl"
        >
          <img
            src={imageUrl}
            alt="Full preview"
            className="w-full max-h-[80vh] object-contain rounded-2xl"
          />
        </motion.div>
      </DialogContent>
    </Dialog>
  );
};

// ═══════════════════════════════════════════════════════════════════
// VOICE WAVEFORM COMPONENT — Real-time audio visualization (ChatGPT-style)
// ═══════════════════════════════════════════════════════════════════

interface VoiceWaveformProps {
  analyserNode: AnalyserNode | null;
  duration: number;
  onStop: () => void;
  onCancel: () => void;
}

const VoiceWaveform: React.FC<VoiceWaveformProps> = ({ analyserNode, duration, onStop, onCancel }) => {
  const canvasRef = React.useRef<HTMLCanvasElement>(null);
  const animationRef = React.useRef<number>(0);
  const historyRef = React.useRef<number[]>([]);

  React.useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !analyserNode) return undefined;

    const ctx = canvas.getContext('2d');
    if (!ctx) return undefined;

    const dataArray = new Uint8Array(analyserNode.fftSize);

    const draw = () => {
      animationRef.current = requestAnimationFrame(draw);

      // Get time-domain data
      analyserNode.getByteTimeDomainData(dataArray);

      // Compute current RMS amplitude (0–1)
      let sum = 0;
      for (let i = 0; i < dataArray.length; i++) {
        const v = ((dataArray[i] ?? 128) - 128) / 128;
        sum += v * v;
      }
      const rms = Math.sqrt(sum / dataArray.length);
      // Amplify and clamp
      const amplitude = Math.min(rms * 4, 1);

      // Push to history (scrolling waveform)
      historyRef.current.push(amplitude);
      // Keep max ~300 points (covers full width at 2px spacing)
      const maxPoints = Math.ceil(canvas.width / 2);
      if (historyRef.current.length > maxPoints) {
        historyRef.current = historyRef.current.slice(-maxPoints);
      }

      const dpr = window.devicePixelRatio || 1;
      const w = canvas.width;
      const h = canvas.height;

      // Clear
      ctx.clearRect(0, 0, w, h);

      const history = historyRef.current;
      const centerY = h / 2;
      const barSpacing = 2.5 * dpr;
      const barWidth = 1.5 * dpr;
      const maxBarHeight = (h / 2) * 0.85;
      const totalBars = history.length;

      // Draw from right to left (newest on right like ChatGPT)
      for (let i = 0; i < totalBars; i++) {
        const x = w - (totalBars - i) * barSpacing;
        if (x < 0) continue;

        const amp = history[i] ?? 0;
        // Minimum bar height for silence (thin line)
        const barH = Math.max(1.5 * dpr, amp * maxBarHeight);

        // Fade out bars towards left edge
        const fadeStart = w * 0.15;
        const alpha = x < fadeStart ? (x / fadeStart) * 0.9 + 0.1 : 1;

        ctx.fillStyle = `rgba(38, 38, 38, ${alpha})`;
        ctx.beginPath();
        // Rounded rect
        const radius = barWidth / 2;
        const y = centerY - barH;
        const fullH = barH * 2;
        ctx.moveTo(x, y + radius);
        ctx.arcTo(x, y, x + barWidth, y, radius);
        ctx.arcTo(x + barWidth, y, x + barWidth, y + fullH, radius);
        ctx.arcTo(x + barWidth, y + fullH, x, y + fullH, radius);
        ctx.arcTo(x, y + fullH, x, y, radius);
        ctx.fill();
      }

      // Draw subtle center line
      ctx.strokeStyle = 'rgba(38, 38, 38, 0.08)';
      ctx.lineWidth = 1 * dpr;
      ctx.beginPath();
      ctx.moveTo(0, centerY);
      ctx.lineTo(w, centerY);
      ctx.stroke();
    };

    draw();

    return () => {
      cancelAnimationFrame(animationRef.current);
    };
  }, [analyserNode]);

  // Handle canvas sizing with DPR
  React.useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;

    const resizeCanvas = () => {
      const dpr = window.devicePixelRatio || 1;
      const rect = canvas.getBoundingClientRect();
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
    };

    resizeCanvas();
    const observer = new ResizeObserver(resizeCanvas);
    observer.observe(canvas);
    return () => observer.disconnect();
  }, []);

  // Reset history when component mounts (new recording)
  React.useEffect(() => {
    historyRef.current = [];
  }, []);

  const formattedDuration = `${Math.floor(duration / 60)}:${String(duration % 60).padStart(2, '0')}`;

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.98 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.98 }}
      transition={{ duration: 0.25, ease: "easeOut" }}
      className="flex items-center gap-3 px-3 py-2 min-h-[44px]"
    >
      {/* Duration */}
      <span className="text-xs font-medium text-[#262626] tabular-nums min-w-[32px] select-none">
        {formattedDuration}
      </span>

      {/* Waveform canvas */}
      <div className="flex-1 h-[36px] relative">
        <canvas
          ref={canvasRef}
          className="w-full h-full"
          style={{ display: 'block' }}
        />
      </div>

      {/* Cancel button */}
      <button
        onClick={onCancel}
        className="h-7 w-7 rounded-full flex items-center justify-center text-[#999999] hover:text-[#262626] hover:bg-[#F0F0F0] transition-all duration-200 shrink-0"
        title="Annuler l'enregistrement"
      >
        <X className="h-3.5 w-3.5" />
      </button>

      {/* Stop & transcribe (checkmark like ChatGPT) */}
      <button
        onClick={onStop}
        className="h-8 w-8 rounded-full bg-[#262626] hover:bg-[#404040] flex items-center justify-center shadow-sm transition-all duration-200 shrink-0"
        title="Terminer et transcrire"
      >
        <Check className="h-4 w-4 text-white" />
      </button>
    </motion.div>
  );
};

// Main HectorPromptInputBox Component
interface HectorPromptInputBoxProps {
  /**
   * Envoi du brouillon. Renvoyer `false` (tout de suite ou via la promesse) le
   * laisse dans le composer : seul un envoi accepté vide le champ et les pièces jointes.
   */
  onSend?: (message: string, files?: File[], documentContext?: import('@/types/documentContext').DocumentContextMultiFilter) => ComposerSendOutcome;
  onStop?: () => void; // 🆕 NOUVEAU: Callback pour arrêter le streaming
  isLoading?: boolean;
  placeholder?: string;
  className?: string;
  /**
   * Reduces vertical whitespace for narrow embedded hosts while preserving the
   * classic Case Discovery controls: attachments, case selector and mic.
   */
  compactHeight?: boolean;
  // Document context props (multiselect)
  documentContext?: import('@/types/documentContext').DocumentContextMultiFilter;
  onDocumentContextChange?: (context: import('@/types/documentContext').DocumentContextMultiFilter) => void;
  // Initial input value - can be set externally
  initialInput?: string;
  onInitialInputConsumed?: () => void; // Called after initial input is set
  /**
   * Pièces jointes posées par l'hôte (parcours « Numéroter avec l'IA » : l'acte
   * déposé dans le sélecteur de source arrive ici plutôt que de partir seul).
   *
   * **REMPLACE** la sélection courante, elle ne s'y ajoute pas : le brouillon de
   * l'hôte est autoritaire, choisir une autre source ne doit jamais cumuler deux
   * actes. Passer `[]` vide donc les pièces jointes.
   */
  initialFiles?: File[];
  onInitialFilesConsumed?: () => void; // Appelé après application de `initialFiles`
  /**
   * Fichiers RENDUS au composeur : un message est parti sans eux, leur
   * téléversement ayant échoué (#2066).
   *
   * S'AJOUTENT en tête de la sélection courante, à l'inverse de `initialFiles` :
   * l'avocat a pu joindre d'autres pièces pendant l'envoi, rien ne doit être
   * écrasé. Passent par l'hôte et non par le retour de `onSend`, parce que le
   * composeur qui a lancé l'envoi est souvent démonté avant sa fin (premier
   * message d'une conversation, changement de conversation) : c'est le composeur
   * monté pour la conversation qui les reprend.
   *
   * L'hôte ne les pose qu'une fois son éventuel brouillon appliqué
   * (`initialFiles`), qui les écraserait sinon — cf.
   * `usePiecesJointesPoseesSurLeComposeur`.
   */
  returnedFiles?: readonly File[];
  /** Appelé avec les fichiers repris, pour que l'hôte cesse de les rendre. */
  onReturnedFilesConsumed?: (files: readonly File[]) => void;
  /**
   * Instantané courant du composeur. Sert au seul relais dock → assistant :
   * l'hôte le lit avant de démonter le dock, sans contrôler ni persister le
   * champ pendant la frappe.
   */
  onDraftChange?: (draft: { readonly content: string; readonly files: readonly File[] }) => void;
  /**
   * Hauteur maximale de la zone de saisie avant scroll interne. Défaut : 240 px
   * (180 en `compactHeight`).
   *
   * Accepte une valeur CSS (`'min(240px, 30vh)'`) et pas seulement un nombre :
   * un plafond en pixels fixes est faux dans un panneau contraint — à 150 % de
   * zoom, un composer de 240 px de haut mangeait tout l'espace et poussait le
   * reste de l'écran d'accueil sous le pli.
   */
  textareaMaxHeight?: number | string;
  // Show document context selector only when a case is selected
  showDocumentSelector?: boolean;
  // Case selection props (for global assistant)
  selectedCase?: { id: string; title: string } | null;
  availableCases?: { id: string; title: string }[];
  onCaseSelect?: (caseId: string | null) => void;
  /** Host contexts may require a dossier for every turn, so they hide deselection. */
  allowCaseClear?: boolean;
  /** Dossiers connexes de la conversation, à côté du dossier principal. */
  relatedCases?: { id: string; title: string }[];
  /** Ajouter un connexe. Absent : pas de contrôle (lecteur, chat verrouillé). */
  onRelatedCaseAdd?: (caseId: string) => void;
  /** Retirer un connexe. Absent : pas de contrôle. */
  onRelatedCaseRemove?: (caseId: string) => void;
  // Quick actions dropdown - show only when NOT on home page (where quick actions are already visible)
  showQuickActionsDropdown?: boolean;
  /** Prompts dynamiques depuis l'API — si non fournis, fallback sur QUICK_ACTION_PROMPTS */
  quickActionPrompts?: UserPrompt[];
  quickActionPromptsLoading?: boolean;
  /** Callback pour réordonner les prompts par drag-and-drop */
  onReorderPrompts?: (items: { id: string; sort_order: number }[]) => Promise<void>;
  /** Callback pour supprimer un prompt depuis le dropdown */
  onDeletePrompt?: (id: string) => Promise<void>;
  /** Callback pour créer un nouveau prompt depuis le dropdown */
  onCreatePrompt?: (data: { title: string; description?: string; prompt_content: string }) => Promise<UserPrompt>;
  /** Callback pour mettre à jour un prompt depuis le dropdown */
  onUpdatePrompt?: (id: string, data: { title?: string; description?: string; prompt_content?: string }) => Promise<UserPrompt>;
  /** Jauge de mémoire de la conversation active (rond à côté du bouton d'envoi). */
  memoryUsage?: ConversationMemoryUsage | null;
  /**
   * Afficher le rond « Mémoire de la conversation ». Décidé par l'hôte : une
   * conversation rouverte a un historique mais ni socket ni mesure.
   */
  showConversationMemory?: boolean;
  /** Offrir « Compacter la conversation » dans le popover du rond. */
  canCompactContext?: boolean;
  /** Compaction manuelle en cours : le rond tourne et l'envoi reste bloqué. */
  compaction?: Pick<ContextCompactionState, 'status'> | null;
  onCompactContext?: () => void;
}

// Export quick action prompts for reuse
export { QUICK_ACTION_PROMPTS };

// Predefined quick action prompts for case discovery
const QUICK_ACTION_PROMPTS = [
  {
    id: "case-overview",
    label: "Vue d'ensemble du dossier",
    description: "Synthèse générale et contexte",
    prompt: `Pouvez-vous me fournir une vue d'ensemble complète de ce dossier juridique ?

J'aimerais une synthèse structurée qui couvre :
- Les faits essentiels
- Le contexte juridique dans lequel s'inscrit l'affaire
- Les dates et événements clés
- Les enjeux majeurs du dossier

Organisez votre réponse de manière claire avec des sections distinctes pour faciliter la compréhension.`
  },
  {
    id: "parties-mapping",
    label: "Cartographie des parties impliquées",
    description: "Acteurs, qualités et prétentions",
    prompt: `Établissez une cartographie détaillée de toutes les parties impliquées dans ce dossier.

Pour chaque partie, précisez :
- Son identité et sa qualité juridique (demandeur, défendeur, appelant, intimé, tiers intervenant, etc.)
- Son rôle dans la procédure
- Ses principales prétentions et demandes
- Les arguments principaux qu'elle invoque

Structurez votre présentation de façon à visualiser clairement les positions respectives de chacun.`
  },
  {
    id: "disputes-analysis",
    label: "Décryptage des litiges",
    description: "Nature et enjeux des conflits",
    prompt: `Analysez en profondeur les différents litiges et points de contentieux présents dans ce dossier.

Pour chaque litige ou chef de demande, identifiez :
- La nature exacte du conflit
- L'objet des prétentions opposées
- Les fondements juridiques invoqués par chaque partie (textes de loi, jurisprudence, principes)
- Les moyens et arguments développés
- Les enjeux juridiques et financiers associés

Présentez votre analyse de façon structurée et hiérarchisée.`
  },
  {
    id: "generate-table",
    label: "Génère un tableau de...",
    description: "Créer un tableau structuré",
    prompt: "Génère un tableau de ..."
  },
  {
    id: "defense-arguments",
    label: "Trouve des arguments pour défendre mon client",
    description: "Arguments exhaustifs avec bases légales",
    prompt: "Trouve moi une liste exhaustive d'arguments permettant de défendre mon client précisant exhaustivement les bases légales et étayée par des citations et preuves. Pour chaque argument légale, prépare une question de droit, précise dans son contexte **juridique** et factuel, qui serait posé après en activant la recherche de Jurisprudences."
  },
  {
    id: "adverse-analysis",
    label: "Analyse les conclusions adverses",
    description: "Contre-arguments et preuves justificatives",
    prompt: "Récapitule de manière très exhaustif les arguments soulevés dans les conclusion adverses, et trouve de manière très exhaustif les contre arguments que je pourrais évoquer, étayé par des preuves justificatives"
  }
];

// Pre-mapped fallback so the array identity is stable across renders (avoids re-allocation on every render)
const QUICK_ACTION_PROMPTS_FALLBACK: UserPrompt[] = QUICK_ACTION_PROMPTS.map(a => ({
  id: a.id,
  title: a.label,
  description: a.description,
  prompt_content: a.prompt,
  sort_order: 0,
  is_default: true,
  created_at: '',
  updated_at: '',
}));

// ─── Sortable row for quick actions dropdown (Notion-style drag handle) ──────

function SortableQARow({
  action,
  onSelect,
  isEditing = false,
  isPendingDelete = false,
  onMarkDelete,
  onUndoDelete,
  onEdit,
}: {
  action: UserPrompt;
  onSelect: (content: string) => void;
  isEditing?: boolean;
  isPendingDelete?: boolean;
  onMarkDelete?: (id: string) => void;
  onUndoDelete?: (id: string) => void;
  onEdit?: (prompt: UserPrompt) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: action.id,
    disabled: isPendingDelete,
  });

  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.4 : 1,
      }}
      className={cn("flex items-stretch group", !isEditing && "cursor-pointer")}
    >
      {/* Poignée de déplacement à la Notion — toujours draggable, indépendamment du mode édition */}
      <div
        {...(!isPendingDelete ? { ...attributes, ...listeners } : {})}
        className={cn(
          "flex items-center pl-2 pr-1 flex-shrink-0 transition-colors text-[#CCCCCC]",
          !isPendingDelete ? "cursor-grab active:cursor-grabbing hover:text-[#999999]" : "cursor-default",
          !isEditing && "group-hover:bg-[#F7F7F7]",
        )}
      >
        <GripVertical className="w-3.5 h-3.5" />
      </div>

      {/* Zone cliquable — hover synchronisé avec le groupe */}
      <button
        type="button"
        onClick={() => { if (!isEditing) onSelect(action.prompt_content); }}
        disabled={isEditing}
        className={cn(
          "flex-1 py-2.5 pr-3 flex flex-col items-start gap-0.5 text-left transition-colors",
          isEditing ? "cursor-default" : "group-hover:bg-[#F7F7F7]",
        )}
      >
        <span className={cn("text-sm font-medium", isPendingDelete ? "line-through text-[#BBBBBB]" : "text-[#262626]")}>
          {action.title}
        </span>
        {action.description && (
          <span className={cn("text-xs", isPendingDelete ? "text-[#CCCCCC]" : "text-[#888888]")}>
            {action.description}
          </span>
        )}
      </button>

      {/* Boutons édition — crayon (modifier) + X/Undo (supprimer).
          Crayon masqué quand la ligne est marquée pour suppression. */}
      {isEditing && (
        <>
          {!isPendingDelete && onEdit && (
            <button
              type="button"
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation();
                onEdit(action);
              }}
              className="flex items-center justify-center w-7 flex-shrink-0 text-[#AAAAAA] hover:text-[#555555] transition-colors"
              aria-label={`Modifier ${action.title}`}
            >
              <Pencil className="w-3.5 h-3.5" />
            </button>
          )}
          <button
            type="button"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              if (isPendingDelete) onUndoDelete?.(action.id);
              else onMarkDelete?.(action.id);
            }}
            className="flex items-center justify-center w-8 flex-shrink-0 text-[#AAAAAA] hover:text-[#555555] transition-colors"
            aria-label={isPendingDelete ? `Annuler suppression de ${action.title}` : `Supprimer ${action.title}`}
          >
            {isPendingDelete ? <Undo2 className="w-3.5 h-3.5" /> : <X className="w-3.5 h-3.5" />}
          </button>
        </>
      )}
    </div>
  );
}

const MAX_SKIPPED_NAMES_SHOWN = 5;

function describeSkippedArchiveEntries(skipped: SkippedArchiveEntry[]): string {
  const listed = skipped.slice(0, MAX_SKIPPED_NAMES_SHOWN).map(entry => `${entry.name} (${entry.reason})`).join(", ");
  const others = skipped.length - MAX_SKIPPED_NAMES_SHOWN;
  const names = others > 0 ? `${listed} et ${others} autre${others > 1 ? "s" : ""}` : listed;
  return skipped.length === 1
    ? `1 fichier de l’archive n’a pas été joint : ${names}.`
    : `${skipped.length} fichiers de l’archive n’ont pas été joints : ${names}.`;
}

export const HectorPromptInputBox = React.forwardRef((props: HectorPromptInputBoxProps, ref: React.Ref<HTMLDivElement>) => {
  const { 
    onSend = () => {}, 
    onStop, // 🆕 NOUVEAU: Fonction pour arrêter le streaming
    isLoading = false, 
    placeholder: _placeholderProp, 
    className,
    compactHeight = false,
    documentContext: documentContextProp,
    initialInput,
    onInitialInputConsumed,
    initialFiles,
    onInitialFilesConsumed,
    returnedFiles,
    onReturnedFilesConsumed,
    onDraftChange,
    textareaMaxHeight,
    // Case selection props
    selectedCase,
    availableCases = [],
    onCaseSelect,
    allowCaseClear = true,
    relatedCases = [],
    onRelatedCaseAdd,
    onRelatedCaseRemove,
    // Quick actions dropdown - default to false (home page doesn't need it)
    showQuickActionsDropdown = false,
    quickActionPrompts,
    quickActionPromptsLoading = false,

    onReorderPrompts,
    onDeletePrompt,
    onCreatePrompt,
    onUpdatePrompt,
    memoryUsage,
    showConversationMemory = false,
    canCompactContext = false,
    compaction,
    onCompactContext,
  } = props;
  const isCompactingContext = compaction?.status === 'running';
  
  const [caseSearchQuery, setCaseSearchQuery] = React.useState("");
  const [showQuickActionsMenu, setShowQuickActionsMenu] = React.useState(false);
  const [showCaseFlyout, setShowCaseFlyout] = React.useState(false);
  // La même liste sert à choisir le principal et à ajouter un connexe.
  const [caseFlyoutPurpose, setCaseFlyoutPurpose] = React.useState<'principal' | 'related'>('principal');
  const [showRelatedList, setShowRelatedList] = React.useState(false);
  // 1 dossier : grande pastille au titre entier ; 2 : petite pastille chacun ; 3 et plus :
  // le principal et un compteur (décision produit du 2026-11-02).
  const caseCount = (selectedCase ? 1 : 0) + relatedCases.length;
  const caseLabels = React.useMemo(() => {
    const listed = [...(selectedCase ? [selectedCase] : []), ...relatedCases];
    const labels = distinctiveCaseLabels(listed.map((item) => item.title));
    return new Map(listed.map((item, index) => [item.id, labels[index] ?? item.title]));
  }, [selectedCase, relatedCases]);
  const flyoutCases = availableCases.filter((caseItem) => (
    caseItem.title.toLowerCase().includes(caseSearchQuery.toLowerCase())
    && (caseFlyoutPurpose === 'principal'
      || (caseItem.id !== selectedCase?.id && !relatedCases.some((related) => related.id === caseItem.id)))
  ));

  // État local pour l'ordre des quick actions dans le dropdown — synchro depuis props quand fermé
  const [localQAPrompts, setLocalQAPrompts] = React.useState<UserPrompt[]>(
    quickActionPrompts ?? QUICK_ACTION_PROMPTS_FALLBACK
  );

  React.useEffect(() => {
    if (!showQuickActionsMenu) {
      setLocalQAPrompts(quickActionPrompts ?? QUICK_ACTION_PROMPTS_FALLBACK);
    }
  }, [quickActionPrompts, showQuickActionsMenu]);

  const qaSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  );

  const handleQADragEnd = React.useCallback(async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = localQAPrompts.findIndex(p => p.id === active.id);
    const newIndex = localQAPrompts.findIndex(p => p.id === over.id);
    const reordered = arrayMove(localQAPrompts, oldIndex, newIndex);
    setLocalQAPrompts(reordered);
    await onReorderPrompts?.(reordered.map((p, i) => ({ id: p.id, sort_order: i })));
  }, [localQAPrompts, onReorderPrompts]);

  // Mode édition inline du dropdown — soft-delete identique à QuickActionsGrid
  const [isEditingQA, setIsEditingQA] = React.useState(false);
  const [pendingQADeletes, setPendingQADeletes] = React.useState<Set<string>>(new Set());
  const [showAddQAModal, setShowAddQAModal] = React.useState(false);
  // Prompt en cours d'édition — null = modale fermée
  const [editingQAPrompt, setEditingQAPrompt] = React.useState<UserPrompt | null>(null);

  const handleQAMarkDelete = React.useCallback((id: string) => {
    setPendingQADeletes(prev => new Set([...prev, id]));
  }, []);

  const handleQAUndoDelete = React.useCallback((id: string) => {
    setPendingQADeletes(prev => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }, []);

  const handleQAExitEdit = React.useCallback(async () => {
    setIsEditingQA(false);
    for (const id of pendingQADeletes) {
      await onDeletePrompt?.(id);
    }
    setPendingQADeletes(new Set());
  }, [pendingQADeletes, onDeletePrompt]);

  const handleQACancelEdit = React.useCallback(() => {
    setIsEditingQA(false);
    setPendingQADeletes(new Set());
    setLocalQAPrompts(quickActionPrompts ?? QUICK_ACTION_PROMPTS_FALLBACK);
  }, [quickActionPrompts]);

  const handleQACreate = React.useCallback(async (data: { title: string; description?: string; prompt_content: string }) => {
    await onCreatePrompt?.(data);
    setShowAddQAModal(false);
  }, [onCreatePrompt]);

  const handleQAUpdate = React.useCallback(async (data: { title: string; description?: string; prompt_content: string }) => {
    if (!editingQAPrompt) return;
    await onUpdatePrompt?.(editingQAPrompt.id, data);
    setEditingQAPrompt(null);
  }, [editingQAPrompt, onUpdatePrompt]);
  
  const getPlaceholder = (): string => {
    return "Décrivez ce que vous voulez faire ...";
  };
  
  const [input, setInput] = React.useState("");
  const { skills: skillCatalog } = useSkillCatalog();
  const [skillCursor, setSkillCursor] = React.useState(0);
  const [activeSkillSlug, setActiveSkillSlug] = React.useState<string | null>(null);
  const [dismissedSlashSignature, setDismissedSlashSignature] = React.useState<string | null>(null);
  const slashToken = React.useMemo(
    () => getSlashToken(input, skillCursor),
    [input, skillCursor],
  );
  const slashSignature = slashToken ? `${input}:${skillCursor}` : null;
  const matchingSkills = React.useMemo(() => {
    if (!slashToken || slashSignature === dismissedSlashSignature) return [];
    return sortSkillsForPalette(filterSkills(skillCatalog, slashToken.query));
  }, [dismissedSlashSignature, skillCatalog, slashSignature, slashToken]);
  const showSkillPalette = slashToken !== null && slashSignature !== dismissedSlashSignature;

  React.useEffect(() => {
    // A Hector skill keeps the same command when it becomes a personal override.
    // Selecting by slug prevents a catalog refetch from moving keyboard focus.
    setActiveSkillSlug((current) => keepActiveSkillSlug(matchingSkills, current));
  }, [matchingSkills]);

  const selectSkill = React.useCallback((skill: ResolvedSkillMetadata) => {
    if (!slashToken) return;
    const nextInput = replaceSlashToken(input, slashToken, skill.slug);
    setInput(nextInput);
    setSkillCursor(slashToken.start + skill.slug.length + 2);
    setDismissedSlashSignature(null);
  }, [input, slashToken]);

  const handleSkillPaletteKeyDown = React.useCallback((event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (!showSkillPalette) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      setDismissedSlashSignature(slashSignature);
      return;
    }
    if (matchingSkills.length === 0) return;
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const direction = event.key === 'ArrowDown' ? 1 : -1;
      setActiveSkillSlug((current) => moveActiveSkillSlug(matchingSkills, current, direction));
      return;
    }
    if (event.key === 'Enter' || event.key === 'Tab') {
      event.preventDefault();
      const selectedSkill = matchingSkills.find((skill) => skill.slug === activeSkillSlug)
        ?? matchingSkills[0];
      if (selectedSkill) selectSkill(selectedSkill);
    }
  }, [activeSkillSlug, matchingSkills, selectSkill, showSkillPalette, slashSignature]);
  
  // Texte posé par l'hôte (raccourcis, brouillon d'un sélecteur de source).
  //
  // La garde porte sur `undefined` — « rien à poser » — et NON sur la chaîne
  // vide : `''` est une valeur utile, c'est ainsi qu'un hôte VIDE le composer
  // (désélectionner sa source doit retirer la consigne qu'elle avait écrite).
  React.useEffect(() => {
    if (initialInput === undefined) return;
    setInput(initialInput);
    onInitialInputConsumed?.();
  }, [initialInput, onInitialInputConsumed]);
  const [files, setFiles] = React.useState<File[]>([]);
  // Layout effect : quand une trame WebSocket d'écriture arrive après un
  // événement de saisie, le parent possède déjà l'instantané que la fermeture
  // synchrone du dock va démonter. Un effet passif laisserait une fenêtre d'un
  // rendu où le dernier caractère ou la dernière pièce jointe disparaît.
  React.useLayoutEffect(() => {
    onDraftChange?.({ content: input, files });
  }, [files, input, onDraftChange]);
  /**
   * Fichiers déposés dont la conversion HEIC → JPEG tourne encore. Ils comptent dans le
   * plafond de documents par message et bloquent l'envoi : un message parti pendant la
   * conversion partirait sans ses photos, qui atterriraient dans le message suivant.
   */
  const [convertingCount, setConvertingCount] = React.useState(0);
  const [filePreviews, setFilePreviews] = React.useState<{ [key: string]: string }>({});
  /**
   * Nombre de brouillons que l'hôte a posés sur ce composeur. Un brouillon posé
   * PENDANT un envoi réaffecte le composeur : le dock d'un dossier y fait passer
   * un autre fil sans le remonter. Si cet envoi est ensuite refusé, son
   * brouillon n'a plus sa place ici — cf. `restoreDraft`.
   */
  const hostDraftCountRef = React.useRef(0);
  /**
   * Pièces jointes posées par l'hôte — cf. `initialFiles`.
   *
   * Pas de `validateMultipleFiles` ici, contrairement à `processMultipleFiles` : l'hôte a
   * déjà validé (type + taille) avant de composer son brouillon, et un toast
   * d'erreur sur un fichier que l'avocat n'a pas déposé DANS le composer serait
   * incompréhensible. Pas d'aperçu non plus — les actes sont des PDF/DOCX.
   */
  React.useEffect(() => {
    if (!initialFiles) return;
    hostDraftCountRef.current += 1;
    setFiles(initialFiles);
    setFilePreviews({});
    onInitialFilesConsumed?.();
  }, [initialFiles, onInitialFilesConsumed]);
  /** Lit l'aperçu des images de `newFiles` ; chaque aperçu est posé dès qu'il est lu. */
  const readImagePreviews = React.useCallback((newFiles: readonly File[]) => {
    newFiles.forEach((file) => {
      if (!file.type.startsWith("image/")) return;
      const reader = new FileReader();
      reader.onload = (e) => {
        const preview = e.target?.result;
        if (typeof preview === "string") setFilePreviews((prev) => ({ ...prev, [file.name]: preview }));
      };
      reader.readAsDataURL(file);
    });
  }, []);
  /**
   * Fichiers rendus par l'hôte — cf. `returnedFiles`.
   *
   * Leurs aperçus sont relus ici : ceux du composeur qui a lancé l'envoi sont
   * partis avec lui. `mergeReturnedFiles` ne double pas un fichier déjà présent,
   * donc rejouer cet effet (StrictMode) est sans conséquence.
   */
  React.useEffect(() => {
    if (!returnedFiles || returnedFiles.length === 0) return;
    setFiles((current) => mergeReturnedFiles(current, returnedFiles));
    readImagePreviews(returnedFiles);
    onReturnedFilesConsumed?.(returnedFiles);
  }, [returnedFiles, onReturnedFilesConsumed, readImagePreviews]);
  const [selectedImage, setSelectedImage] = React.useState<string | null>(null);
  const [documentContext, setDocumentContext] = React.useState<DocumentContextMultiFilter>(
    documentContextProp || DEFAULT_DOCUMENT_MULTI_CONTEXT
  );
  const uploadInputRef = React.useRef<HTMLInputElement>(null);
  const promptBoxRef = React.useRef<HTMLDivElement>(null);
  const [isTranscriptionFading, setIsTranscriptionFading] = React.useState(false);

  // Voice recording integration
  const handleVoiceTranscription = React.useCallback((text: string) => {
    // Trigger fade-in animation
    setIsTranscriptionFading(true);
    setInput(text);
    // Remove animation class after it completes
    setTimeout(() => setIsTranscriptionFading(false), 600);
  }, []);

  const voiceRecording = useVoiceRecording({
    context: 'case-discovery',
    language: 'fr',
    onTranscription: handleVoiceTranscription,
  });

  // Sync with prop changes
  React.useEffect(() => {
    if (documentContextProp) {
      setDocumentContext(documentContextProp);
    }
  }, [documentContextProp]);

  // Close menus when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      // Close case selector if clicking outside
      if (showCaseFlyout && !target.closest('[data-case-selector]')) {
        setShowCaseFlyout(false);
      }
      // Close quick actions menu if clicking outside
      if (showQuickActionsMenu && !target.closest('[data-quick-actions-menu]')) {
        setShowQuickActionsMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showCaseFlyout, showQuickActionsMenu]);

  const processMultipleFiles = React.useCallback(async (newFiles: File[]) => {
    // Filter out system files, hidden files, and AppleDouble meta-files (.DS_Store, Thumbs.db, _filename.pdf, etc.)
    const droppedFiles = newFiles.filter(file =>
      !file.name.startsWith('.') && !file.name.startsWith('_')
    );
    if (droppedFiles.length === 0 && newFiles.length > 0) return;
    // Le dock d'un dossier passe d'un fil à l'autre sans remonter ce composeur : un
    // brouillon posé pendant l'ouverture d'une archive ou une conversion signe un
    // autre fil, où ces fichiers n'ont rien à faire.
    const draftAtStart = hostDraftCountRef.current;

    // Les archives s'ouvrent avant la validation : le plafond de 50 documents
    // compte leurs fichiers, pas l'archive. Pendant l'ouverture, l'envoi attend.
    const zipCount = droppedFiles.filter(isZipFile).length;
    let filteredFiles = droppedFiles;
    if (zipCount > 0) {
      const openingToast = toast.loading("Ouverture de l'archive…");
      setConvertingCount(count => count + zipCount);
      const expansion = await expandChatAttachmentArchives(droppedFiles).finally(() => {
        setConvertingCount(count => count - zipCount);
        toast.dismiss(openingToast);
      });
      if (expansion.skipped.length > 0) {
        toast.warning(describeSkippedArchiveEntries(expansion.skipped), { ...toastDuration(10000) });
      }
      if (hostDraftCountRef.current !== draftAtStart) return;
      filteredFiles = expansion.files;
      if (filteredFiles.length === 0) return;
    }

    // Le plafond de nombre porte sur le MESSAGE entier, d'où les fichiers déjà
    // joints et ceux encore en conversion passés à la validation ; format et
    // poids se jugent fichier par fichier.
    // Un ajout refusé n'ajoute rien — pas même le début du lot.
    const validation = validateMultipleFiles(filteredFiles, {
      existingFiles: files,
      pendingCount: convertingCount,
    });
    if (!validation.isValid) {
      toast.error(validation.error || "Erreur de validation des fichiers", { ...toastDuration(8000) });
      return;
    }
    
    // Photos iPhone : converties en JPEG avant d'entrer dans le composer, pour que la
    // vignette s'affiche (Chrome et Firefox ne dessinent pas le HEIC) et que l'envoi suive
    // le chemin d'un JPEG. Cf. utils/heicConversion.ts.
    const heicCount = filteredFiles.filter(isHeicFile).length;
    const conversionToast = heicCount > 0
      ? toast.loading(heicCount > 1 ? `Conversion de ${heicCount} photos iPhone en JPEG…` : "Conversion de la photo iPhone en JPEG…")
      : undefined;
    setConvertingCount(count => count + filteredFiles.length);
    const { files: readyFiles, failures } = await convertHeicFilesToJpeg(filteredFiles).finally(() => {
      setConvertingCount(count => count - filteredFiles.length);
      if (conversionToast !== undefined) toast.dismiss(conversionToast);
    });
    failures.forEach(failure => toast.error(failure.message, { ...toastDuration(10000) }));
    if (readyFiles.length === 0 || hostDraftCountRef.current !== draftAtStart) return;

    // Add new files to existing files instead of replacing. Mise à jour fonctionnelle :
    // la conversion est asynchrone, `files` a pu changer pendant l'attente.
    setFiles(prev => [...prev, ...readyFiles]);
    readImagePreviews(readyFiles);
  }, [files, convertingCount, readImagePreviews]);

  const handleDragOver = React.useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDragLeave = React.useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  /**
   * Le glisser-déposer emprunte exactement le même chemin que le sélecteur de fichiers.
   *
   * Il filtrait auparavant sur une liste en dur (PDF/DOC/DOCX/images) qui ignorait la
   * moitié des formats réellement acceptés — `.msg`, `.eml`, `.txt`, tableurs, audio —
   * et les écartait **sans le moindre message** : l'avocat déposait son e-mail Outlook et
   * ne voyait rien se passer. `processMultipleFiles` délègue déjà à `validateMultipleFiles`
   * (extension + taille), qui nomme le fichier et les formats autorisés dans son toast.
   * Un format refusé doit être dit, pas avalé.
   */
  const handleDrop = React.useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const droppedFiles = Array.from(e.dataTransfer.files);
    if (droppedFiles.length > 0) void processMultipleFiles(droppedFiles);
  }, [processMultipleFiles]);

  const handleRemoveFile = (index: number) => {
    const fileToRemove = files[index];
    if (fileToRemove && filePreviews[fileToRemove.name]) {
      setFilePreviews(prev => {
        const newPreviews = { ...prev };
        delete newPreviews[fileToRemove.name];
        return newPreviews;
      });
    }
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const openImageModal = (imageUrl: string) => setSelectedImage(imageUrl);

  const handlePaste = React.useCallback((e: ClipboardEvent) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (!item) continue;
      if (item.type.indexOf("image") !== -1) {
        const file = item.getAsFile();
        if (file) {
          e.preventDefault();
          void processMultipleFiles([file]);
          break;
        }
      }
    }
  }, [processMultipleFiles]);

  React.useEffect(() => {
    document.addEventListener("paste", handlePaste);
    return () => document.removeEventListener("paste", handlePaste);
  }, [handlePaste]);

  const handleSubmit = async () => {
    // Garde d'envoi : un tour en cours bloque l'envoi même si le textarea est
    // éditable (le bouton affiche Stop, et Entrée est déjà gardé côté textarea).
    if (isLoading || convertingCount > 0) return;
    if (input.trim() || files.length > 0) {
      const submittedInput = input;
      const submittedFiles = files;
      const submittedPreviews = filePreviews;
      const hostDraftCountAtSubmit = hostDraftCountRef.current;

      // Le plafond de nombre est relu à l'envoi, et c'est cette lecture qui
      // fait autorité : la validation de l'ajout ne voit pas les fichiers posés
      // par l'hôte (`initialFiles`). Le brouillon reste à l'écran.
      const limits = validateMultipleFiles([], { existingFiles: submittedFiles });
      if (!limits.isValid) {
        toast.error(limits.error || "Erreur de validation des fichiers", { ...toastDuration(8000) });
        return;
      }

      submitComposerDraft({
        send: () => onSend(submittedInput, submittedFiles, documentContext),
        clearDraft: () => {
          setInput("");
          setFiles([]);
          setFilePreviews({});
        },
        // Envoi refusé après coup : le brouillon revient, sans écraser ce que
        // l'avocat a pu commencer à taper ou joindre entre-temps. Les fichiers
        // du message refusé reviennent TOUS, en tête de ceux joints depuis :
        // ne les rendre qu'à un composeur vide les faisait disparaître dès que
        // l'avocat avait joint une pièce pendant l'envoi.
        //
        // Sauf si l'hôte a posé un brouillon depuis l'envoi : ce composeur sert
        // alors un autre fil, et y rendre le message refusé mêlerait ses pièces
        // et son texte au message d'une autre conversation.
        restoreDraft: () => {
          if (hostDraftCountRef.current !== hostDraftCountAtSubmit) return;
          setInput((current) => (current.trim() === "" ? submittedInput : current));
          setFiles((current) => mergeReturnedFiles(current, submittedFiles));
          setFilePreviews((current) => ({ ...submittedPreviews, ...current }));
        },
      });
    }
  };

  const hasContent = input.trim() !== "" || files.length > 0;

  const canSend = hasContent && !isLoading && convertingCount === 0;

  return (
    <>
      <PromptInput
        value={input}
        onValueChange={setInput}
        isLoading={isLoading}
        maxHeight={textareaMaxHeight ?? (compactHeight ? 180 : 240)}
        onSubmit={handleSubmit}
        className={cn(
          "w-full bg-white border-[#D9D9D9] shadow-[0_8px_30px_rgba(0,0,0,0.12)] transition-all duration-300 ease-in-out",
          compactHeight && "!rounded-2xl !p-1.5",
          className
        )}
        // PAS `disabled={isLoading}` : la zone de saisie reste éditable pendant que
        // l'agent travaille (préparer le prochain message). `isLoading` gouverne le
        // seul blocage d'ENVOI (bouton Stop + gardes Enter/handleSubmit). `disabled`
        // est réservé à un vrai état désactivé du champ (aucun ici).
        ref={ref || promptBoxRef}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {files.length > 0 && (
          <div className="p-0 pb-1 transition-all duration-300">
            {/* File count indicator */}
            <div className="text-xs text-[#666666] mb-3 px-3">
              {files.length} fichier{files.length > 1 ? 's' : ''} sélectionné{files.length > 1 ? 's' : ''} (max {CHAT_ATTACHMENT_LIMITS.maxFilesPerMessage})
            </div>
            {/* Hauteur bornée, défilement interne : sans elle, 50 documents
                repousseraient la zone de saisie et le bouton d'envoi hors du
                panneau (dock du dossier surtout). Le compteur reste au-dessus,
                toujours visible. */}
            <div
              data-testid="composer-attachments"
              className="flex flex-wrap gap-2 max-h-[min(136px,25vh)] overflow-y-auto"
            >
              {files.filter(file => file && file.name).map((file, index) => (
                <div key={`${file.name}-${index}`} className="relative group">
                  {file?.type?.startsWith("image/") && filePreviews[file.name] ? (
                    <div
                      className="w-16 h-16 rounded-xl overflow-hidden cursor-pointer transition-all duration-300"
                      onClick={() => openImageModal(filePreviews[file.name] ?? '')}
                    >
                      <img
                        src={filePreviews[file.name]}
                        alt={file.name}
                        className="h-full w-full object-cover"
                      />
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveFile(index);
                        }}
                        className="absolute top-1 right-1 rounded-full bg-black/70 p-0.5 opacity-100 transition-opacity"
                      >
                        <X className="h-3 w-3 text-white" />
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 bg-[#F7F7F7] px-3 py-2 rounded-xl border border-[#D9D9D9]">
                      <div className={cn(
                        "w-6 h-6 rounded flex items-center justify-center",
                        "bg-grey-50"
                      )}>
                        {file.type.startsWith("image/") ? (
                          <Image className="w-3 h-3 text-grey-600" />
                        ) : (
                          <FileText className="w-3 h-3 text-grey-600" />
                        )}
                      </div>
                      <span className="text-sm text-[#262626] max-w-[120px] truncate">{file.name}</span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveFile(index);
                        }}
                        className="rounded-full bg-transparent hover:bg-black/10 p-0.5 transition-colors"
                      >
                        <X className="h-3 w-3 text-[#666666]" />
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        <AnimatePresence initial={false}>
          {voiceRecording.state === 'recording' && (
            /* ── Waveform replaces textarea during recording ── */
            <VoiceWaveform
              key="voice-waveform"
              analyserNode={voiceRecording.analyserNode}
              duration={voiceRecording.recordingDuration}
              onStop={() => voiceRecording.stopRecording()}
              onCancel={() => voiceRecording.cancelRecording()}
            />
          )}
          {voiceRecording.state === 'processing' && (
            /* ── Processing / transcribing state ── */
            <motion.div
              key="voice-processing"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
              className="flex items-center justify-center gap-2.5 px-3 py-3 min-h-[44px]"
            >
              <Loader2 className="h-4 w-4 text-[#262626] animate-spin" />
              <span className="text-sm text-[#666666] font-medium">Transcription en cours...</span>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Normal textarea — always mounted, hidden only during voice recording/processing */}
        <div
          className={cn(
            "relative transition-opacity duration-150",
            (voiceRecording.state === 'recording' || voiceRecording.state === 'processing')
              ? "opacity-0 h-0 overflow-hidden pointer-events-none"
              : "opacity-100"
          )}
        >
          <PromptInputTextarea
            placeholder={getPlaceholder()}
            onChange={(event) => {
              setInput(event.target.value);
              setSkillCursor(event.target.selectionStart);
              setDismissedSlashSignature(null);
            }}
            onClick={(event) => setSkillCursor(event.currentTarget.selectionStart)}
            onKeyUp={(event) => setSkillCursor(event.currentTarget.selectionStart)}
            onKeyDown={handleSkillPaletteKeyDown}
            className={cn(
              "text-sm",
              compactHeight && "!min-h-[38px] !max-h-[180px] !py-1.5 !overflow-y-auto",
              isTranscriptionFading && "animate-in fade-in-0 duration-500"
            )}
          />
          {showSkillPalette ? (
            <SkillSlashPalette
              skills={matchingSkills}
              activeSlug={keepActiveSkillSlug(matchingSkills, activeSkillSlug)}
              onSelect={selectSkill}
            />
          ) : null}
        </div>

        {/* Hide entire action bar during recording/processing — controls are inline in waveform */}
        <PromptInputActions
          className={cn(
            "flex items-center justify-between gap-1 p-0 pt-2 transition-all duration-200 min-w-0",
            compactHeight && "pt-1",
            (voiceRecording.state === 'recording' || voiceRecording.state === 'processing') && "opacity-0 h-0 overflow-hidden pt-0 pointer-events-none"
          )}
        >
          <div className="flex items-center gap-1 transition-opacity duration-300 opacity-100 visible min-w-0 flex-wrap">
            {/* Attach File Icon */}
            <PromptInputAction tooltip={`Joindre des fichiers (PDF, DOCX, Excel, images) • Max ${formatChatAttachmentSize(CHAT_ATTACHMENT_LIMITS.maxFileSizeBytes)} par fichier`}>
              <button
                onClick={() => uploadInputRef.current?.click()}
                className="flex h-8 w-8 text-[#666666] cursor-pointer items-center justify-center rounded-full transition-all duration-200 hover:bg-[#F7F7F7] hover:text-[#262626]"
              >
                <Paperclip className="h-4 w-4 transition-colors" />
                <input
                  ref={uploadInputRef}
                  type="file"
                  className="hidden"
                  onChange={(e) => {
                    if (e.target.files && e.target.files.length > 0) {
                      const fileArray = Array.from(e.target.files);
                      void processMultipleFiles(fileArray);
                    }
                    if (e.target) e.target.value = "";
                  }}
                  multiple
                  accept=".zip,application/zip,.pdf,.docx,.doc,.xlsx,.xls,.csv,.png,.jpg,.jpeg,.gif,.webp,.eml,.msg,.mp3,.m4a,.wav,.ogg,.opus,.flac,.webm,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/msword,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/csv,message/rfc822,application/vnd.ms-outlook,image/*,audio/*"
                />
              </button>
            </PromptInputAction>

            {/* Bouton Dossier — sélecteur indépendant, avec les dossiers connexes */}
            {onCaseSelect && (
              <div className="relative flex min-w-0 flex-wrap items-center gap-1" data-case-selector>
                <button
                  type="button"
                  title={selectedCase ? `Dossier principal : ${selectedCase.title}` : undefined}
                  onClick={() => {
                    setCaseFlyoutPurpose('principal');
                    setShowCaseFlyout(!showCaseFlyout || caseFlyoutPurpose !== 'principal');
                  }}
                  className={cn(
                    "rounded-full transition-all duration-200 flex items-center gap-1.5",
                    caseCount >= 2
                      ? "h-7 max-w-[240px] border border-[#262626]/15 bg-[#F7F7F7] pl-2.5 pr-1 text-[#262626] hover:bg-[#EFEFEF]"
                      : "h-8 px-2 @[750px]:px-3 bg-transparent text-[#666666] hover:bg-[#F7F7F7] hover:text-[#262626]",
                  )}
                >
                  <FolderOpen className={cn("shrink-0", caseCount >= 2 ? "h-3.5 w-3.5" : "h-4 w-4")} />
                  <span className={cn(
                    "truncate",
                    caseCount >= 2 ? "text-xs font-medium" : "text-sm font-medium",
                    caseCount >= 2 ? null : compactHeight ? "max-w-[80px]" : "max-w-[320px]",
                  )}>
                    {selectedCase ? (caseLabels.get(selectedCase.id) ?? selectedCase.title) : "Sélectionner un dossier"}
                  </span>
                  {/* La marque n'a de sens qu'à côté d'un autre dossier. */}
                  {selectedCase && relatedCases.length > 0 && (
                    <span className="shrink-0 text-[11px] font-normal text-[#999999]">principal</span>
                  )}
                  {selectedCase && allowCaseClear && (
                    <span
                      role="button"
                      aria-label="Retirer le dossier principal de la conversation"
                      onClick={(e) => {
                        e.stopPropagation();
                        onCaseSelect(null);
                      }}
                      className="ml-0.5 rounded-full hover:bg-[#E5E5E5] p-0.5 transition-colors"
                    >
                      <X className="h-3 w-3" />
                    </span>
                  )}
                </button>

                {/* Deux dossiers : une petite pastille chacun. Trois et plus : le principal
                    et un compteur, pour que le composeur garde une seule ligne. */}
                {caseCount === 2 && relatedCases.map((relatedCase) => (
                  <span
                    key={relatedCase.id}
                    title={`Dossier connexe : ${relatedCase.title}`}
                    className="flex h-7 max-w-[240px] items-center gap-1 rounded-full border border-[#E5E5E5] pl-2.5 pr-1 text-xs text-[#666666]"
                  >
                    <span className="truncate">{caseLabels.get(relatedCase.id) ?? relatedCase.title}</span>
                    {onRelatedCaseRemove && (
                      <button
                        type="button"
                        aria-label={`Retirer le dossier « ${relatedCase.title} » de la conversation`}
                        onClick={() => onRelatedCaseRemove(relatedCase.id)}
                        className="rounded-full p-0.5 transition-colors hover:bg-[#E5E5E5]"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    )}
                  </span>
                ))}

                {caseCount >= 3 && relatedCases.length > 0 && (
                  <Popover open={showRelatedList} onOpenChange={setShowRelatedList}>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        aria-label={`Voir les ${relatedCases.length} dossiers connexes`}
                        className="flex h-7 items-center gap-1 rounded-full border border-dashed border-[#CCCCCC] px-2.5 text-xs text-[#666666] transition-colors hover:bg-[#F7F7F7] hover:text-[#262626]"
                      >
                        +{relatedCases.length} dossier{relatedCases.length > 1 ? 's' : ''}
                        <ChevronDown className="h-3 w-3" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent align="start" side="top" className="w-80 p-0">
                      <div className="border-b border-[#F0F0F0] px-3 py-2 text-[10px] font-semibold uppercase tracking-wide text-[#999999]">
                        Dossiers connexes
                      </div>
                      <div className="max-h-56 overflow-y-auto py-1">
                        {relatedCases.map((relatedCase) => (
                          <div
                            key={relatedCase.id}
                            title={`Dossier connexe : ${relatedCase.title}`}
                            className="flex items-center gap-2 px-3 py-1.5 hover:bg-[#F7F7F7]"
                          >
                            <span className="min-w-0 flex-1 truncate text-sm text-[#262626]">{relatedCase.title}</span>
                            {onRelatedCaseRemove && (
                              <button
                                type="button"
                                aria-label={`Retirer le dossier « ${relatedCase.title} » de la conversation`}
                                onClick={() => onRelatedCaseRemove(relatedCase.id)}
                                className="rounded p-1 text-[#999999] transition-colors hover:text-[#262626]"
                              >
                                <X className="h-3.5 w-3.5" />
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                      {onRelatedCaseAdd && (
                        <button
                          type="button"
                          aria-label="Ajouter un dossier à la conversation"
                          onClick={() => {
                            setShowRelatedList(false);
                            setCaseFlyoutPurpose('related');
                            setShowCaseFlyout(true);
                          }}
                          className="flex w-full items-center gap-1.5 border-t border-[#F0F0F0] px-3 py-2 text-left text-sm text-[#666666] transition-colors hover:bg-[#F7F7F7] hover:text-[#262626]"
                        >
                          <Plus className="h-3.5 w-3.5" />
                          Ajouter un dossier
                        </button>
                      )}
                    </PopoverContent>
                  </Popover>
                )}

                {/* Sans dossier, la pastille ci-dessus pose le premier : un second bouton ferait double emploi.
                    À partir de trois, l'ajout vit dans la liste du compteur. */}
                {onRelatedCaseAdd && (selectedCase || relatedCases.length > 0) && caseCount < 3 && (
                  <button
                    type="button"
                    aria-label="Ajouter un dossier à la conversation"
                    title="Ajouter un dossier à la conversation"
                    onClick={() => {
                      setCaseFlyoutPurpose('related');
                      setShowCaseFlyout(!showCaseFlyout || caseFlyoutPurpose !== 'related');
                    }}
                    className="flex h-7 w-7 items-center justify-center rounded-full text-[#666666] transition-colors hover:bg-[#F7F7F7] hover:text-[#262626]"
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                )}

                {/* Case Dropdown */}
                <AnimatePresence>
                  {showCaseFlyout && (
                    <motion.div
                      initial={{ opacity: 0, y: -4 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -4 }}
                      transition={{ duration: 0.15 }}
                      className="absolute bottom-full left-0 mb-2 w-64 bg-white rounded-xl shadow-md border border-[#E5E5E5] overflow-hidden z-[200]"
                    >
                      {/* Search */}
                      <div className="px-3 py-2 border-b border-[#F0F0F0]">
                        <input
                          type="text"
                          placeholder="Rechercher..."
                          value={caseSearchQuery}
                          onChange={(e) => setCaseSearchQuery(e.target.value)}
                          className="w-full px-2.5 py-1.5 text-sm bg-[#F7F7F7] rounded-lg border-none focus:outline-none"
                        />
                      </div>

                      {/* Cases List */}
                      <div className="max-h-48 overflow-y-auto py-1">
                        {flyoutCases
                          .map((caseItem) => (
                            <button
                              key={caseItem.id}
                              type="button"
                              onClick={() => {
                                if (caseFlyoutPurpose === 'related') {
                                  onRelatedCaseAdd?.(caseItem.id);
                                } else {
                                  // A host-selected context can change dossiers, but it must
                                  // never silently clear the target for the next turn.
                                  onCaseSelect(selectedCase?.id === caseItem.id && allowCaseClear ? null : caseItem.id);
                                }
                                setCaseSearchQuery("");
                                setShowCaseFlyout(false);
                              }}
                              className={cn(
                                "w-full px-3 py-2 text-left text-sm hover:bg-[#F7F7F7] flex items-center gap-2.5 transition-colors",
                                selectedCase?.id === caseItem.id && "bg-[#F7F7F7]"
                              )}
                            >
                              <FolderOpen className="h-4 w-4 text-[#666666] shrink-0" />
                              <span className="truncate text-[#333333]">{caseItem.title}</span>
                              {selectedCase?.id === caseItem.id && (
                                <Check className="h-3.5 w-3.5 text-[#262626] shrink-0 ml-auto" />
                              )}
                            </button>
                          ))}

                        {flyoutCases.length === 0 && (
                          <div className="px-3 py-4 text-sm text-[#999999] text-center">
                            Aucun dossier trouvé
                          </div>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}

          </div>

          {/* Quick Actions + Send Button - grouped together with minimal gap */}
          <div className="flex items-center gap-0.5 shrink-0">
            {showConversationMemory && (
              <ConversationMemoryControl
                memoryUsage={memoryUsage ?? null}
                canCompactContext={canCompactContext}
                compactionStatus={compaction?.status ?? 'idle'}
                // `isLoading` inclut la compaction elle-même ; le contrôle la
                // distingue par `compactionStatus` pour afficher « en cours ».
                isBusy={isLoading && !isCompactingContext}
                onCompactContext={onCompactContext}
              />
            )}

            {/* Quick Actions Dropdown - Only show when NOT on home page */}
            {showQuickActionsDropdown && (
              <div className="relative" data-quick-actions-menu>
                <button
                  type="button"
                  onClick={() => setShowQuickActionsMenu(!showQuickActionsMenu)}
                  aria-label="Actions rapides"
                  aria-expanded={showQuickActionsMenu}
                  title="Actions rapides"
                  className={cn(
                    // Sous 750px le libellé disparaît. Le bouton prend alors le gabarit
                    // rond de 32 px du rond de mémoire et du bouton d'envoi qui l'encadrent :
                    // une pastille oblongue entre deux ronds cassait la ligne d'actions,
                    // surtout quand le canvas réduit la colonne de conversation.
                    "h-8 w-8 @[750px]:w-auto @[750px]:px-3 rounded-full transition-all duration-200 flex items-center justify-center gap-2",
                    showQuickActionsMenu
                      ? "bg-[#F7F7F7] text-[#262626]"
                      : "bg-transparent text-[#666666] hover:bg-[#F7F7F7] hover:text-[#262626]"
                  )}
                >
                  {/* Même icône « bibliothèque » que le composeur de l'add-in Word
                      (ChatInputArea) : l'avocat retrouve le même repère d'une surface
                      à l'autre pour la même liste de prompts. Le bécher n'évoquait rien. */}
                  <Library className="h-4 w-4 @[750px]:hidden" />
                  <span className="text-sm font-medium hidden @[750px]:inline">Actions rapides</span>
                  <ChevronDown className={cn(
                    "h-3.5 w-3.5 transition-transform duration-200 hidden @[750px]:inline",
                    showQuickActionsMenu && "rotate-180"
                  )} />
                </button>

                {/* Quick Actions Dropdown Menu */}
                <AnimatePresence>
                  {showQuickActionsMenu && (
                    <motion.div
                      initial={{ opacity: 0, y: 4 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 4 }}
                      transition={{ duration: 0.15 }}
                      className="absolute bottom-full right-0 mb-2 w-72 bg-white rounded-xl shadow-sm border border-[#E5E5E5] overflow-hidden z-50"
                    >
                      {/* Header en mode édition */}
                      {isEditingQA && (
                        <div className="flex items-center justify-between px-3 py-2 border-b border-[#E5E5E5]">
                          <button
                            type="button"
                            onClick={handleQACancelEdit}
                            className="text-xs text-[#888888] hover:text-[#444444] transition-colors"
                          >
                            Annuler
                          </button>
                          <button
                            type="button"
                            onClick={handleQAExitEdit}
                            className={cn(
                              "text-xs font-semibold transition-colors",
                              pendingQADeletes.size > 0 ? "text-red-500 hover:text-red-700" : "text-[#262626] hover:text-[#555555]",
                            )}
                          >
                            {pendingQADeletes.size > 0 ? `Supprimer (${pendingQADeletes.size})` : 'Terminer'}
                          </button>
                        </div>
                      )}

                      <div className="py-1">
                        {quickActionPromptsLoading ? (
                          <div className="px-3 py-4 text-sm text-[#888888] text-center">Chargement...</div>
                        ) : (
                          <DndContext sensors={qaSensors} collisionDetection={closestCenter} onDragEnd={handleQADragEnd}>
                            <SortableContext items={localQAPrompts.map(a => a.id)} strategy={verticalListSortingStrategy}>
                              {localQAPrompts.map((action) => (
                                <SortableQARow
                                  key={action.id}
                                  action={action}
                                  isEditing={isEditingQA}
                                  isPendingDelete={pendingQADeletes.has(action.id)}
                                  onMarkDelete={handleQAMarkDelete}
                                  onUndoDelete={handleQAUndoDelete}
                                  onEdit={onUpdatePrompt ? setEditingQAPrompt : undefined}
                                  onSelect={(content) => {
                                    setInput(content);
                                    setShowQuickActionsMenu(false);
                                  }}
                                />
                              ))}
                            </SortableContext>
                          </DndContext>
                        )}

                        {/* Bouton "+" en mode édition */}
                        {isEditingQA && onCreatePrompt && (
                          <button
                            type="button"
                            onClick={() => setShowAddQAModal(true)}
                            className="w-full px-3 py-2.5 flex items-center gap-2 text-xs text-[#888888] hover:bg-[#F7F7F7] transition-colors border-t border-[#E5E5E5]"
                          >
                            <Plus className="w-3.5 h-3.5" />
                            Ajouter une action rapide
                          </button>
                        )}
                      </div>

                      {/* Pied du dropdown — bouton Gérer (mode normal uniquement) */}
                      {!isEditingQA && (
                        <button
                          type="button"
                          onClick={() => setIsEditingQA(true)}
                          className="w-full px-3 py-2.5 text-xs text-[#888888] hover:bg-[#F7F7F7] border-t border-[#E5E5E5] text-left transition-colors flex items-center gap-1.5"
                        >
                          <Settings2 className="w-3 h-3" />
                          Gérer
                        </button>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Modal pour créer une action rapide depuis le dropdown */}
                {onCreatePrompt && (
                  <Dialog open={showAddQAModal} onOpenChange={(open) => { if (!open) setShowAddQAModal(false); }}>
                    <DialogContent className="max-w-xl p-0 gap-0 overflow-hidden" hideCloseButton>
                      <div className="px-5 py-4 border-b border-grey-100">
                        <h3 className="text-sm font-semibold text-grey-900">Nouvelle action rapide</h3>
                      </div>
                      <div className="px-5 py-4">
                        <AddPromptForm
                          onSave={handleQACreate}
                          onCancel={() => setShowAddQAModal(false)}
                        />
                      </div>
                    </DialogContent>
                  </Dialog>
                )}

                {/* Modal d'édition d'une action rapide — réutilise AddPromptForm avec initialValues.
                    key={editingQAPrompt.id} force le remount pour reset l'état interne du form
                    quand l'utilisateur passe d'un prompt à l'autre. */}
                {onUpdatePrompt && (
                  <Dialog open={editingQAPrompt !== null} onOpenChange={(open) => { if (!open) setEditingQAPrompt(null); }}>
                    <DialogContent className="max-w-xl p-0 gap-0 overflow-hidden" hideCloseButton>
                      <div className="px-5 py-4 border-b border-grey-100">
                        <h3 className="text-sm font-semibold text-grey-900">Modifier l'action rapide</h3>
                      </div>
                      <div className="px-5 py-4">
                        {editingQAPrompt && (
                          <AddPromptForm
                            key={editingQAPrompt.id}
                            initialValues={{
                              title: editingQAPrompt.title,
                              description: editingQAPrompt.description ?? '',
                              prompt_content: editingQAPrompt.prompt_content,
                            }}
                            onSave={handleQAUpdate}
                            onCancel={() => setEditingQAPrompt(null)}
                            saveLabel="Sauvegarder"
                          />
                        )}
                      </div>
                    </DialogContent>
                  </Dialog>
                )}
              </div>
            )}

            {/* Send / Stop / Voice Button */}
            <AnimatePresence mode="wait" initial={false}>
              {isLoading && isCompactingContext ? (
                /* ── COMPACTION : l'envoi est bloqué mais aucun tour ne tourne —
                   « Arrêter la génération » mentirait et n'interromprait rien. ── */
                <motion.div
                  key="compaction-btn"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.08 }}
                >
                  <button
                    type="button"
                    disabled
                    aria-label="Compaction de la mémoire en cours"
                    className="h-8 w-8 rounded-full bg-[#E5E5E5] text-[#666666] flex items-center justify-center cursor-wait"
                  >
                    <Loader2 className="h-4 w-4 animate-spin" />
                  </button>
                </motion.div>
              ) : isLoading ? (
                /* ── STOP button — NOT wrapped in PromptInputAction to avoid disabled context ── */
                <motion.div
                  key="stop-btn"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.08 }}
                >
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        className="h-8 w-8 rounded-full bg-[#262626] hover:bg-[#404040] text-white shadow-sm flex items-center justify-center cursor-pointer"
                        onClick={(e) => {
                          if (e.detail > 1) return;
                          onStop?.();
                        }}
                        onPointerDown={(e) => {
                          const btn = e.currentTarget;
                          if (!btn.dataset.ready) {
                            e.preventDefault();
                            e.stopPropagation();
                          }
                        }}
                        ref={(el) => {
                          if (el && !el.dataset.ready) {
                            setTimeout(() => { if (el) el.dataset.ready = 'true'; }, 350);
                          }
                        }}
                      >
                        <Square className="h-3.5 w-3.5 fill-white" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="top">Arrêter la génération</TooltipContent>
                  </Tooltip>
                </motion.div>
              ) : hasContent ? (
                /* ── SEND button (user typed text) ── */
                <motion.div
                  key="send-btn"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.08 }}
                >
                  <PromptInputAction tooltip="Envoyer le message">
                    <button
                      className={cn(
                        "h-8 w-8 rounded-full flex items-center justify-center",
                        canSend
                          ? "bg-[#262626] hover:bg-[#333333] text-white shadow-sm"
                          : "bg-[#E5E5E5] text-[#999999] cursor-not-allowed"
                      )}
                      onClick={() => { if (canSend) void handleSubmit(); }}
                      disabled={!canSend}
                    >
                      <Send className="h-4 w-4" />
                    </button>
                  </PromptInputAction>
                </motion.div>
              ) : (
                /* ── MIC button (input empty — default state) ── */
                <motion.div
                  key="mic-btn"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.08 }}
                >
                  <PromptInputAction tooltip="Dictée vocale">
                    <button
                      className="h-8 w-8 rounded-full bg-[#F7F7F7] hover:bg-[#E5E5E5] text-[#666666] hover:text-[#262626] flex items-center justify-center transition-colors disabled:opacity-50 disabled:cursor-wait"
                      onClick={() => voiceRecording.startRecording()}
                      // Désactivé pendant l'ouverture du micro : un second clic lançait un second enregistreur.
                      disabled={voiceRecording.state === 'starting'}
                      aria-busy={voiceRecording.state === 'starting'}
                    >
                      <Mic className="h-4 w-4" />
                    </button>
                  </PromptInputAction>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </PromptInputActions>

      </PromptInput>

      <ImageViewDialog imageUrl={selectedImage} onClose={() => setSelectedImage(null)} />
    </>
  );
});
HectorPromptInputBox.displayName = "HectorPromptInputBox";
